'use client';
import { useError } from '@/lib/context/ErrorContextProvider.client';
import { useLoading } from '@/lib/context/LoadingContextProvider.client';
import { logging } from '@/lib/logging/LogManager';
import { logError } from '@/lib/logging/Logger';
import { reactMarkdownConfig } from '@/lib/utils/strapi';
import {
  Badge,
  Button,
  Checkbox,
  CheckboxGroup,
  Chip,
  Input,
  Spinner,
  Textarea,
} from '@heroui/react';
import { motion } from 'framer-motion';
import { TFunction } from 'i18next';
import Image from 'next/image';
import ReactMarkdown from 'react-markdown';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createContactFormSchema, type ContactFormData } from '@/lib/contactForm';
const logger = logging.getLogger('fe-contactSection');
import { inspect } from 'util';
import AlertBox from '@/components/AlertBox';

const containerVariants = {
  hidden: { opacity: 0, x: -100 },
  visible: { opacity: 1, x: 0, transition: { delay: 0.3 } },
};

enum MessageStatus {
  Sent = 1,
  Error = 2,
  NotSend = 4,
}

type State = {
  // isVisible: boolean;
  // validated: boolean;
  sent: MessageStatus;
  interval?: NodeJS.Timeout;
};

const ContactSection = ({
  t,
  title,
  cta,
  // frontend,
  // backend,
  // devInf,
  // devOps,
  // mainText,
}: {
  t: TFunction<'translation', undefined>;
  title: string;
  cta: string;
  // frontend: string;
  // backend: string;
  // devInf: string;
  // devOps: string;
  // mainText: string;
}) => {
  const { loading, setLoading } = useLoading();
  const { error, setError } = useError();
  const [state, setState] = useState<State>({
    // isVisible: true,
    // validated: false,
    sent: MessageStatus.NotSend,
  });

  // const phoneRegex = new RegExp(/^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/);

  const FormSchema = createContactFormSchema({
    emailRequired: t('contact.form.email_required'),
    emailInvalid: t('contact.form.email_invalid'),
    nameRequired: t('contact.form.name'),
    nameInvalid: t('contact.form.name'),
    nameTooShort: t('contact.form.name_too_short'),
    nameTooLong: t('contact.form.name_too_long'),
    telephoneTooLong: t('contact.form.telephone_too_long'),
    messageRequired: t('contact.form.message'),
    messageTooShort: t('contact.form.message_too_short'),
    messageTooLong: t('contact.form.message_too_long'),
    checkedRequired: t('contact.form.check_error'),
    honeypotTriggered: t('contact.message-error'),
  });
  // const { t } = useTranslation();

  const {
    register,
    handleSubmit: handleReactHookFormSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    control,
  } = useForm<ContactFormData>({
    resolver: zodResolver(FormSchema), // Apply the zodResolver
    defaultValues: {
      email: '',
      name: '',
      telephone: '',
      message: '',
      checked: false,
      website: '',
    },
  });

  const handleSubmit: SubmitHandler<ContactFormData> = async (data: ContactFormData) => {
    // console.error('handle submit');
    if (loading) return;
    setLoading(true);
    setError(false);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        setState({ ...state, sent: MessageStatus.Sent });
        // setContent('');
        // ref.current?.setMarkdown('');
        // setUserTags([]);
        // router.refresh();
        reset();
        setLoading(false);
        return;
      }
      throw Error(res.statusText);
    } catch (error) {
      setState({ ...state, sent: MessageStatus.Error });
      logError(logger, 'Fehler beim Anlegen der Nachricht', error);
      setError('Fehler beim Anlegen der Nachricht');
    } finally {
      setLoading(false);
    }
  };
  // console.log(inspect(errors));
  return (
    <div
      className="section bg-gradient-to-t from-content1 to-content2 text-current text-center relative overflow-hidden" // Tailwind classes for styling
    >
      <div className="absolute inset-0">
        <Image
          src={'/map-image.png'}
          alt={'Map'}
          className="object-cover object-center opacity-30 bg-gradient-to-t from-black to-background w-full h-full"
          fill
        />
      </div>
      <div className="container mx-auto px-4 lg:px-14 relative z-10">
        <motion.div
          initial="hidden"
          whileInView={'visible'}
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <form onSubmit={handleReactHookFormSubmit(handleSubmit)} className="w-full" noValidate>
            <h2 className="section-title">{title}</h2>
              <div className="section-body section-pad">
                <div className="section-block text-left">
                  <ReactMarkdown className="section-prose" {...reactMarkdownConfig}>
                    {cta}
                  </ReactMarkdown>
                </div>
                {/* <h3 className="mt-14 font-semibold">{t('services.intro_tech')}</h3> */}
                <div className="section-block grid gap-6 lg:grid-cols-2">
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                    {...register('website')}
                  />
                  <div className="flex flex-col space-y-6">
                    <Input
                      type="email"
                      label="Email"
                      placeholder={t('email')}
                      errorMessage={errors.email?.message}
                      {...register('email')}
                      isInvalid={Boolean(errors.email?.message)}
                    />
                    <Input
                      type="text"
                      label="Name"
                      placeholder={t('name')}
                      errorMessage={errors.name?.message}
                      {...register('name')}
                      isInvalid={Boolean(errors.name?.message)}
                    />
                    <Input
                      type="text"
                      label="Tel"
                      placeholder={t('telephone-number') + ' (' + t('optional') + ')'}
                      errorMessage={errors.telephone?.message}
                      {...register('telephone')}
                      isInvalid={Boolean(errors.telephone?.message)}
                    />
                  </div>
                  <div className="flex flex-col">
                    {/* <div className="flex-grow"> */}
                    <Textarea
                      // minRows={9}
                      // maxRows={100}
                      disableAutosize
                      label={t('contact.form.message-label')}
                      placeholder={t('contact.enter-message')}
                      classNames={{
                        base: 'flex-grow',
                        // input: 'flex-grow flex-1',
                        inputWrapper: 'flex-grow',
                      }}
                      errorMessage={errors.message?.message}
                      isInvalid={Boolean(errors.message?.message)}
                      {...register('message')}
                    />

                    {/* <TextAreaGroup name="bio" label="Bio" /> */}
                    {/* </div> */}
                  </div>
                </div>
                {/* <Card className="mt-8">
                <CardBody>
                  <Checkbox>Option</Checkbox>
                </CardBody>
              </Card> */}
                <Controller
                  control={control}
                  name="checked"
                  render={({ field: { onChange, value } }) => (
                    <div className="section-block px-3 py-4 sm:py-2 ">
                      <CheckboxGroup
                        errorMessage={errors.checked?.message}
                        isInvalid={Boolean(errors.checked?.message)}
                      >
                        <Checkbox
                          className="rounded-2xl border-2 hover:bg-content2/[.9]"
                          onChange={onChange}
                          isSelected={value}
                        >
                          <span className="text-foreground-500 text-small">
                            {t('contact.form.check')}
                          </span>
                        </Checkbox>
                      </CheckboxGroup>
                    </div>
                  )}
                />

                <div className="section-block text-left">
                  <Button
                    color="primary"
                    variant="flat"
                    type="submit"
                    // isDisabled={!isDirty || !isValid}
                    className="min-w-24 mt-12"
                  >
                    {loading ? <Spinner size="sm" /> : t('contact.send-message')}
                  </Button>
                </div>
              </div>
            </form>
            {state.sent === MessageStatus.Sent ? (
              <AlertBox
                type="success"
                timer={4000} // Omit for a persistent alert
              >
                <Chip color="success">{t('contact.message-sent')}</Chip>
              </AlertBox>
            ) : state.sent === MessageStatus.Error ? (
              <AlertBox
                type="alert"
                timer={4000} // Omit for a persistent alert
              >
                <h1>
                  <motion.div initial="hidden" animate="visible" variants={containerVariants}>
                    <Chip color="danger">{t('contact.message-error')}</Chip>
                  </motion.div>
                </h1>
              </AlertBox>
            ) : null}
          </motion.div>
          {/* <div>{inspect(errors)}</div> */}
      </div>
      {/* Other sections with motion.div */}
    </div>
  );
};

// interface InputGroup1Props extends InputHTMLAttributes<HTMLInputElement> {
//   label: string;
//   name: string;
//   value?: string;
//   onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
// }

// function InputGroup1({
//   label,
//   name,
//   value,
//   onChange,
//   type = 'text',
//   disabled,
//   ...props // Capture additional props here
// }: InputGroup1Props) {
//   return (
//     <div className="relative z-30 w-full">
//       <div className="flex flex-col h-full">
//         <input
//           type={type}
//           id={name}
//           name={name}
//           value={value}
//           onChange={onChange}
//           disabled={disabled}
//           className={`peer block py-2.5 px-1 w-full text-sm text-gray-600 bg-transparent border-0 border-b-[2px] appearance-none focus:outline-none focus:ring-0 focus:border-[#FF6464] ${
//             disabled ? 'border-gray-300' : 'border-gray-400'
//           }`}
//           placeholder=" "
//           {...props} // Spread additional props to the input element
//         />
//         <label
//           htmlFor={name}
//           className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-8 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-[#FF6464] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
//         >
//           {label}
//         </label>
//       </div>
//     </div>
//   );
// }

// interface TextAreaGroupProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
//   label: string;
//   name: string;
//   value?: string;
//   onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
// }

// function TextAreaGroup({ label, name, value, onChange, disabled, ...props }: TextAreaGroupProps) {
//   return (
//     <div className="relative z-30 h-full">
//       <div className="flex flex-col h-full">
//         <textarea
//           id={name}
//           name={name}
//           value={value}
//           onChange={onChange}
//           disabled={disabled}
//           placeholder=" "
//           className={`peer resize-none flex-1 block py-2.5 px-1 w-full text-sm text-gray-600 bg-transparent border-0 border-b-2 border-gray-400 appearance-none focus:outline-none focus:ring-0 focus:border-[#FF6464] ${disabled ? 'border-gray-300' : 'border-gray-400'} ${props.className}`}
//           {...props}
//         />
//         <label
//           htmlFor={name}
//           className="peer-focus:font-medium absolute text-sm text-gray-500 transition-transform duration-300 transform scale-75 -translate-y-12 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-[#FF6464] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
//         >
//           {label}
//         </label>
//       </div>
//     </div>
//   );
// }
export default ContactSection;
